import { useState, useEffect, useCallback, useRef } from "react";
import axios from "../../axios";

// ─── Config ────────────────────────────────────────────────────────────────
const API_BASE = "/api/jobs"; // Change this to your full base URL if needed

// ─── Sort option → API param map ──────────────────────────────────────────
const SORT_MAP = {
  newest:   { sortBy: "jobPostDate",     order: "desc" },
  expiring: { sortBy: "expirationDate",  order: "asc"  },
  popular:  { sortBy: "impressionsCount", order: "desc" },
};

// ─── Static lists (match Mongoose enum) ───────────────────────────────────
const ALL_CATEGORIES = [
  "Easy Apply","Remote","IT","Government","Part-time","Hybrid","HR",
  "Small biz","Manufacturing","Sustainability","Construction","Pharma",
  "Finance","Biotech","Media","Retail","Education","Hospitality",
].sort();

const EXPERIENCE_LEVELS = [
  "Internship","Entry Level","Associate","Mid-Senior Level","Director","Executive",
];

// ─── Helpers ───────────────────────────────────────────────────────────────
const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const daysUntil = (date) => Math.ceil((new Date(date) - new Date()) / 86400000);

const timeAgo = (date) => {
  const days = Math.floor((Date.now() - new Date(date)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days}d ago`;
};

const formatImpressions = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

// ─── Debounce hook ─────────────────────────────────────────────────────────
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Category colour map ───────────────────────────────────────────────────
const CATEGORY_COLORS = {
  IT:             { bg: "#e8f0ff", text: "#006bff" },
  Hybrid:         { bg: "#f0f0f0", text: "#444" },
  HR:             { bg: "#fff0f6", text: "#c2185b" },
  Finance:        { bg: "#e8fff4", text: "#1a7f4b" },
  Biotech:        { bg: "#f3e8ff", text: "#7c3aed" },
  Sustainability: { bg: "#eafff3", text: "#166534" },
  "Part-time":    { bg: "#fff8e1", text: "#b45309" },
  Construction:   { bg: "#fff3e0", text: "#c2440e" },
  Remote:         { bg: "#e8f0ff", text: "#006bff" },
  Government:     { bg: "#f0f4ff", text: "#3730a3" },
  Manufacturing:  { bg: "#fef2f2", text: "#991b1b" },
  Pharma:         { bg: "#fdf4ff", text: "#86198f" },
  Media:          { bg: "#fffbeb", text: "#92400e" },
  Retail:         { bg: "#ecfeff", text: "#0e7490" },
  Education:      { bg: "#f0fdf4", text: "#166534" },
  Hospitality:    { bg: "#fff1f2", text: "#be123c" },
  "Small biz":    { bg: "#f7f7f7", text: "#555" },
  "Easy Apply":   { bg: "#dcfce7", text: "#15803d" },
};
const getCatStyle = (cat) => CATEGORY_COLORS[cat] || { bg: "#f0f0f0", text: "#555" };

// ─── Small SVG icons ───────────────────────────────────────────────────────
const Icon = {
  Search:  () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Loc:     () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Clock:   () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Eye:     () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Dollar:  (c) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c || "#006bff"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  Link:    () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#006bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  Ext:     () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  Level:   () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  Domain:  () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  EyeB:    () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Bag:     () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  Refresh: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  ChevL:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  ChevR:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
};

// ─── Skeleton Card ─────────────────────────────────────────────────────────
const SkeletonCard = ({ delay = 0 }) => (
  <div style={{
    background: "#fff",
    border: "1.5px solid #e8e8e8",
    borderRadius: 14,
    padding: "22px 22px 18px",
    display: "flex", flexDirection: "column", gap: 14,
    animation: "pulse 1.6s ease-in-out infinite",
    animationDelay: `${delay}s`,
  }}>
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div style={{ width: 80, height: 22, background: "#f0f0f0", borderRadius: 20 }} />
      <div style={{ width: 70, height: 16, background: "#f0f0f0", borderRadius: 6 }} />
    </div>
    <div>
      <div style={{ width: "70%", height: 20, background: "#f0f0f0", borderRadius: 6, marginBottom: 8 }} />
      <div style={{ width: "45%", height: 14, background: "#f5f5f5", borderRadius: 6 }} />
    </div>
    <div style={{ width: 120, height: 28, background: "#f0f0f0", borderRadius: 8 }} />
    <div style={{ display: "flex", gap: 6 }}>
      {[60, 80, 55, 70].map((w, i) => (
        <div key={i} style={{ width: w, height: 22, background: "#f5f5f5", borderRadius: 6 }} />
      ))}
    </div>
    <div style={{
      display: "flex", justifyContent: "space-between",
      paddingTop: 10, borderTop: "1px solid #f0f0f0",
    }}>
      <div style={{ width: 70, height: 14, background: "#f5f5f5", borderRadius: 6 }} />
      <div style={{ width: 50, height: 14, background: "#f5f5f5", borderRadius: 6 }} />
    </div>
  </div>
);

// ─── Category Pill ─────────────────────────────────────────────────────────
const CategoryPill = ({ category }) => {
  const { bg, text } = getCatStyle(category);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "3px 10px", background: bg, color: text,
      borderRadius: 20, fontSize: 11, fontWeight: 700,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      {category}
    </span>
  );
};

// ─── Tag Chip ──────────────────────────────────────────────────────────────
const Tag = ({ label }) => (
  <span style={{
    padding: "3px 9px", background: "#f4f4f4", color: "#555",
    borderRadius: 6, fontSize: 11, fontWeight: 500,
    fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
  }}>
    {label}
  </span>
);

// ─── Detail panel helpers ──────────────────────────────────────────────────
const MetaChip = ({ icon, label }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 11px", background: "#f7f7f7", borderRadius: 8,
    fontSize: 12, fontWeight: 500, color: "#444",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    {icon} {label}
  </span>
);

const SectionHeading = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
    textTransform: "uppercase", color: "#aaa",
    fontFamily: "'DM Sans', sans-serif",
    marginBottom: 12, paddingBottom: 8, borderBottom: "1.5px solid #f0f0f0",
  }}>
    {children}
  </div>
);

const DateCard = ({ label, value, urgent, sub }) => (
  <div style={{
    background: urgent ? "#fff5f5" : "#f9f9f9",
    border: `1.5px solid ${urgent ? "#fecaca" : "#eee"}`,
    borderRadius: 10, padding: "12px 14px",
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 700, color: urgent ? "#dc2626" : "#000", fontFamily: "'DM Sans', sans-serif", marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: urgent ? "#dc2626" : "#aaa", fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── Job Card ──────────────────────────────────────────────────────────────
const JobCard = ({ job, onClick, index }) => {
  const [hovered, setHovered] = useState(false);
  const expires = daysUntil(job.expirationDate);
  const isUrgent = expires <= 7;

  return (
    <div
      onClick={() => onClick(job)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        border: `1.5px solid ${hovered ? "#006bff" : "#e8e8e8"}`,
        borderRadius: 14,
        padding: "22px 22px 18px",
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
        boxShadow: hovered ? "0 8px 32px rgba(0,107,255,0.10)" : "0 1px 4px rgba(0,0,0,0.04)",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        display: "flex", flexDirection: "column", gap: 14,
        animationDelay: `${index * 0.05}s`,
        animation: "cardIn 0.35s ease both",
        position: "relative", overflow: "hidden",
      }}
    >
      {/* Blue left-edge accent on hover */}
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
        background: "#006bff", borderRadius: "3px 0 0 3px",
        opacity: hovered ? 1 : 0, transition: "opacity 0.2s",
      }} />

      {/* Top row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <CategoryPill category={job.jobCategory} />
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: isUrgent ? "#dc2626" : "#999",
          fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap",
        }}>
          {isUrgent ? `⚡ ${expires}d left` : `Expires ${formatDate(job.expirationDate)}`}
        </span>
      </div>

      {/* Title */}
      <div>
        <h3 style={{
          margin: 0, fontSize: 16, fontWeight: 700,
          color: hovered ? "#006bff" : "#000",
          fontFamily: "'DM Sans', serif",
          lineHeight: 1.25, transition: "color 0.2s", letterSpacing: "-0.01em",
        }}>
          {job.jobTitle}
        </h3>
        <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#666", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
            <Icon.Loc /> {job.jobLocation}
          </span>
          <span style={{ fontSize: 12, color: "#666", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
            <Icon.Clock /> {job.requirements?.experienceLevel}
          </span>
        </div>
      </div>

      {/* Salary */}
      <div style={{
        display: "inline-flex", alignItems: "center",
        padding: "5px 12px",
        background: job.salaryBracket === "Not Disclosed" ? "#f9f9f9" : "#e8f0ff",
        borderRadius: 8, alignSelf: "flex-start",
      }}>
        <span style={{
          fontSize: 12, fontWeight: 700,
          color: job.salaryBracket === "Not Disclosed" ? "#999" : "#006bff",
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {job.salaryBracket === "Not Disclosed" ? "Salary not disclosed" : job.salaryBracket}
        </span>
      </div>

      {/* Tags */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {(job.tags || []).slice(0, 4).map((t) => <Tag key={t} label={t} />)}
        {(job.tags || []).length > 4 && (
          <span style={{ fontSize: 11, color: "#aaa", alignSelf: "center", fontFamily: "'DM Sans', sans-serif" }}>
            +{job.tags.length - 4}
          </span>
        )}
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        paddingTop: 10, borderTop: "1px solid #f0f0f0",
      }}>
        <span style={{ fontSize: 11, color: "#aaa", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
          <Icon.Eye /> {formatImpressions(job.impressionsCount)} views
        </span>
        <span style={{ fontSize: 11, color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>
          {timeAgo(job.jobPostDate)}
        </span>
      </div>
    </div>
  );
};

// ─── Detail Panel ──────────────────────────────────────────────────────────
const DetailPanel = ({ job, onClose }) => {
  const expires = daysUntil(job.expirationDate);
  const isUrgent = expires <= 7;

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)",
        zIndex: 100, animation: "fadeIn 0.2s ease",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(640px, 100vw)", background: "#fff",
        zIndex: 101, overflowY: "auto",
        boxShadow: "-8px 0 48px rgba(0,0,0,0.12)",
        animation: "slideIn 0.3s cubic-bezier(0.22,1,0.36,1)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Sticky header */}
        <div style={{
          position: "sticky", top: 0, background: "#fff",
          borderBottom: "1.5px solid #f0f0f0",
          padding: "16px 28px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          zIndex: 10,
        }}>
          <CategoryPill category={job.jobCategory} />
          <button onClick={onClose} style={{
            background: "#f4f4f4", border: "none", borderRadius: 8,
            width: 34, height: 34, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#555", fontSize: 18, lineHeight: 1,
            transition: "background 0.15s",
          }}>
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "28px 28px 40px", flex: 1 }}>
          <h2 style={{
            margin: "0 0 12px",
            fontSize: 28, fontFamily: "'DM Sans', serif",
            fontWeight: 700, color: "#000",
            lineHeight: 1.15, letterSpacing: "-0.02em",
          }}>
            {job.jobTitle}
          </h2>

          {/* Meta chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
            <MetaChip icon={<Icon.Loc />}    label={job.jobLocation} />
            <MetaChip icon={<Icon.Level />}  label={job.requirements?.experienceLevel} />
            <MetaChip icon={<Icon.Domain />} label={job.requirements?.experienceDomain} />
            <MetaChip icon={<Icon.EyeB />}   label={`${formatImpressions(job.impressionsCount)} impressions`} />
          </div>

          {/* Salary */}
          <div style={{
            background: job.salaryBracket === "Not Disclosed" ? "#f9f9f9" : "#e8f0ff",
            borderRadius: 12, padding: "14px 18px", marginBottom: 24,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <Icon.Dollar color={job.salaryBracket === "Not Disclosed" ? "#aaa" : "#006bff"} />
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>Salary</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: job.salaryBracket === "Not Disclosed" ? "#999" : "#006bff", fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>
                {job.salaryBracket}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
            <DateCard label="Posted" value={formatDate(job.jobPostDate)} />
            <DateCard
              label="Expires" value={formatDate(job.expirationDate)}
              urgent={isUrgent}
              sub={isUrgent ? `${expires} days left!` : `${expires} days remaining`}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 28 }}>
            <SectionHeading>About the role</SectionHeading>
            <div
              className="detail-desc"
              style={{ fontSize: 14, lineHeight: 1.8, color: "#333", fontFamily: "'DM Sans', sans-serif" }}
              dangerouslySetInnerHTML={{ __html: job.jobDescription || "<p>No description provided.</p>" }}
            />
          </div>

          {/* Tags */}
          {(job.tags || []).length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <SectionHeading>Skills &amp; Keywords</SectionHeading>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {job.tags.map((t) => (
                  <span key={t} style={{
                    padding: "5px 12px", background: "#f0f5ff",
                    color: "#006bff", borderRadius: 8,
                    fontSize: 12, fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* URL preview */}
          <div style={{
            padding: "14px 18px",
            border: "1.5px dashed #dde7ff", borderRadius: 12,
            marginBottom: 28,
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <Icon.Link />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", fontFamily: "'DM Sans', sans-serif" }}>Job Post URL</div>
              <div style={{ fontSize: 12, color: "#666", fontFamily: "'DM Sans', sans-serif", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {job.jobPostLink}
              </div>
            </div>
          </div>

          {/* Primary CTA */}
          <a
            href={job.jobPostLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              padding: "15px 28px", background: "#006bff", color: "#fff",
              borderRadius: 12, textDecoration: "none",
              fontSize: 15, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
              letterSpacing: "0.01em",
              transition: "background 0.2s, transform 0.15s, box-shadow 0.2s",
              boxShadow: "0 4px 16px rgba(0,107,255,0.30)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#0055cc";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,107,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#006bff";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,107,255,0.30)";
            }}
          >
            <span>View Full Job Post</span>
            <Icon.Ext />
          </a>
          <p style={{ textAlign: "center", fontSize: 11, color: "#bbb", marginTop: 12, fontFamily: "'DM Sans', sans-serif" }}>
            Opens on the employer's website in a new tab
          </p>
        </div>
      </div>
    </>
  );
};

// ─── Pagination ────────────────────────────────────────────────────────────
const Pagination = ({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { currentPage, totalPages, totalCount, limit } = pagination;
  const start = (currentPage - 1) * limit + 1;
  const end = Math.min(currentPage * limit, totalCount);

  // Build page numbers with ellipsis
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
  }

  const btnStyle = (active, disabled) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 34, height: 34, borderRadius: 8,
    border: `1.5px solid ${active ? "#006bff" : "#e8e8e8"}`,
    background: active ? "#006bff" : "#fff",
    color: active ? "#fff" : disabled ? "#ccc" : "#444",
    fontWeight: 600, fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      paddingTop: 32, flexWrap: "wrap", gap: 12,
    }}>
      <span style={{ fontSize: 12, color: "#999", fontFamily: "'DM Sans', sans-serif" }}>
        Showing <strong style={{ color: "#333" }}>{start}–{end}</strong> of <strong style={{ color: "#333" }}>{totalCount}</strong> jobs
      </span>
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <button
          style={btnStyle(false, !pagination.hasPrevPage)}
          onClick={() => pagination.hasPrevPage && onPageChange(currentPage - 1)}
          disabled={!pagination.hasPrevPage}
        >
          <Icon.ChevL />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} style={{ fontSize: 13, color: "#ccc", padding: "0 4px" }}>…</span>
          ) : (
            <button key={p} style={btnStyle(p === currentPage, false)} onClick={() => onPageChange(p)}>
              {p}
            </button>
          )
        )}
        <button
          style={btnStyle(false, !pagination.hasNextPage)}
          onClick={() => pagination.hasNextPage && onPageChange(currentPage + 1)}
          disabled={!pagination.hasNextPage}
        >
          <Icon.ChevR />
        </button>
      </div>
    </div>
  );
};

// ─── Error State ───────────────────────────────────────────────────────────
const ErrorState = ({ message, onRetry }) => (
  <div style={{
    textAlign: "center", padding: "80px 20px",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    <div style={{ fontSize: 38, marginBottom: 14 }}>⚠️</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 6 }}>
      Failed to load jobs
    </div>
    <div style={{ fontSize: 13, color: "#999", marginBottom: 20, maxWidth: 320, margin: "0 auto 20px" }}>
      {message || "Something went wrong. Please try again."}
    </div>
    <button
      onClick={onRetry}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "9px 20px", background: "#006bff", color: "#fff",
        border: "none", borderRadius: 8, cursor: "pointer",
        fontSize: 13, fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <Icon.Refresh /> Try again
    </button>
  </div>
);

// ─── Empty State ───────────────────────────────────────────────────────────
const EmptyState = ({ onClear }) => (
  <div style={{
    textAlign: "center", padding: "80px 20px",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    <div style={{ fontSize: 38, marginBottom: 14 }}>🔍</div>
    <div style={{ fontSize: 16, fontWeight: 600, color: "#aaa", marginBottom: 16 }}>
      No jobs match your filters
    </div>
    <button
      onClick={onClear}
      style={{
        padding: "8px 18px", background: "#006bff", color: "#fff",
        border: "none", borderRadius: 8, cursor: "pointer",
        fontSize: 13, fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      Clear filters
    </button>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────
export default function JobBoard() {
  // ── UI state ──
  const [selectedJob, setSelectedJob]       = useState(null);
  const [searchInput, setSearchInput]       = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeLevel, setActiveLevel]       = useState("All");
  const [sortOption, setSortOption]         = useState("newest");
  const [page, setPage]                     = useState(1);

  // ── Data state ──
  const [jobs, setJobs]             = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // Debounce the search input — only fires the API call 450ms after typing stops
  const debouncedSearch = useDebounce(searchInput, 450);

  // ── Reset to page 1 whenever any filter changes ──
  useEffect(() => { setPage(1); }, [debouncedSearch, activeCategory, activeLevel, sortOption]);

  // ── Build query string and fetch ──────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { sortBy, order } = SORT_MAP[sortOption];

    const params = new URLSearchParams({ page, limit: 20, sortBy, order });

    if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
    if (activeCategory !== "All")  params.set("category", activeCategory);
    if (activeLevel    !== "All")  params.set("level",    activeLevel);

    try {
      const res = await axios.get(`/job/allJobs?${params.toString()}`);

      if (!res.data.success) throw new Error(res.data.message || "Unexpected response from server");

      setJobs(res.data?.data);
      setPagination(res.data?.pagination);
    } catch (err) {
      setError(err.message);
      setJobs([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, activeCategory, activeLevel, sortOption]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // ── Scroll to top of grid on page change ──
  const gridRef = useRef(null);
  useEffect(() => {
    if (!loading && gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [page, loading]);

  const handleClearFilters = () => {
    setSearchInput("");
    setActiveCategory("All");
    setActiveLevel("All");
    setSortOption("newest");
    setPage(1);
  };

  const hasActiveFilters =
    searchInput.trim() !== "" || activeCategory !== "All" || activeLevel !== "All";

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes cardIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        @keyframes slideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .jb-search:focus {
          outline: none;
          border-color: #006bff !important;
          box-shadow: 0 0 0 3px rgba(0,107,255,0.10);
        }

        .jb-sort { outline: none; }
        .jb-sort:focus { border-color: #006bff !important; }

        .jb-cat-btn {
          border: 1.5px solid #e8e8e8;
          border-radius: 20px;
          padding: 6px 14px;
          cursor: pointer;
          font-size: 12px; font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.15s;
          background: #fff; color: #555;
          white-space: nowrap;
        }
        .jb-cat-btn:hover { border-color: #006bff; color: #006bff; }
        .jb-cat-btn.active { background: #006bff; border-color: #006bff; color: #fff; }

        .jb-level-btn {
          border: none; background: none;
          padding: 5px 10px; cursor: pointer;
          font-size: 12px; font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          color: #999; border-radius: 6px;
          transition: all 0.15s; white-space: nowrap;
        }
        .jb-level-btn:hover { background: #f0f0f0; color: #333; }
        .jb-level-btn.active { background: #000; color: #fff; }

        .detail-desc h2 {
          font-family: 'DM Sans', serif;
          font-size: 17px; color: #000; margin: 18px 0 8px;
        }
        .detail-desc ul, .detail-desc ol { padding-left: 20px; }
        .detail-desc li { margin-bottom: 4px; }
        .detail-desc p { margin-bottom: 10px; }

        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #ddd; border-radius: 10px; }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#f8f8f8", paddingBottom: 80 }}>

        <div ref={gridRef} style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px 0" }}>

          {/* ── Hero ── */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{
              fontFamily: "'DM Sans', serif",
              fontSize: "clamp(30px, 4vw, 46px)",
              fontWeight: 700, color: "#000",
              lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 6,
            }}>
              Find your next <span style={{ color: "#006bff" }}>opportunity.</span>
            </h1>
            <p style={{ fontSize: 14, color: "#777", fontFamily: "'DM Sans', sans-serif" }}>
              Curated openings across tech, finance, science, and more — updated live.
            </p>
          </div>

          {/* ── Search + Sort ── */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flex: "1 1 260px", minWidth: 220 }}>
              <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#aaa", display: "flex" }}>
                <Icon.Search />
              </span>
              <input
                className="jb-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search jobs, skills, location…"
                style={{
                  width: "100%",
                  padding: "10px 14px 10px 36px",
                  border: "1.5px solid #e8e8e8",
                  borderRadius: 10, fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif",
                  color: "#000", background: "#fff",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
              />
              {/* Debounce indicator */}
              {searchInput !== debouncedSearch && (
                <span style={{
                  position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                  width: 8, height: 8, background: "#006bff", borderRadius: "50%",
                  opacity: 0.7,
                }} />
              )}
            </div>

            <select
              className="jb-sort"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              style={{
                padding: "10px 32px 10px 14px",
                border: "1.5px solid #e8e8e8", borderRadius: 10,
                fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                color: "#333",
                background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' viewBox='0 0 24 24'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 10px center`,
                appearance: "none", cursor: "pointer",
                transition: "border-color 0.2s", flexShrink: 0,
              }}
            >
              <option value="newest">Newest first</option>
              <option value="expiring">Expiring soon</option>
              <option value="popular">Most viewed</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                style={{
                  padding: "10px 14px",
                  border: "1.5px solid #e8e8e8", borderRadius: 10,
                  background: "#fff", color: "#999",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  display: "flex", alignItems: "center", gap: 5,
                  transition: "all 0.15s", flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#dc2626"; e.currentTarget.style.color = "#dc2626"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.color = "#999"; }}
              >
                × Clear filters
              </button>
            )}
          </div>

          {/* ── Category Filters ── */}
          <div style={{ marginBottom: 12, overflowX: "auto", paddingBottom: 4 }}>
            <div style={{ display: "flex", gap: 7, width: "max-content" }}>
              <button
                className={`jb-cat-btn${activeCategory === "All" ? " active" : ""}`}
                onClick={() => setActiveCategory("All")}
              >
                All Categories
              </button>
              {ALL_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  className={`jb-cat-btn${activeCategory === cat ? " active" : ""}`}
                  onClick={() => setActiveCategory(activeCategory === cat ? "All" : cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* ── Level Filters ── */}
          <div style={{ marginBottom: 28, display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#ccc", textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4, fontFamily: "'DM Sans', sans-serif" }}>
              Level
            </span>
            {["All", ...EXPERIENCE_LEVELS].map((l) => (
              <button
                key={l}
                className={`jb-level-btn${activeLevel === l ? " active" : ""}`}
                onClick={() => setActiveLevel(l)}
              >
                {l}
              </button>
            ))}
          </div>

          {/* ── Grid content ── */}
          {error ? (
            <ErrorState message={error} onRetry={fetchJobs} />
          ) : loading ? (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 16,
            }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} delay={i * 0.07} />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <EmptyState onClear={handleClearFilters} />
          ) : (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 16,
              }}>
                {jobs.map((job, i) => (
                  <JobCard key={job._id} job={job} onClick={setSelectedJob} index={i} />
                ))}
              </div>

              <Pagination pagination={pagination} onPageChange={setPage} />
            </>
          )}
        </div>
      </div>

      {/* ── Detail Panel ── */}
      {selectedJob && (
        <DetailPanel job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </>
  );
}